import React, { useEffect, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import CommentSection from './CommentSection';
import PageHeader from 'app/design/speedBot/PageHeader';
import MuiBreadcrumbs from 'app/design/uiComponents/MuiBreadcrumbs';
import Breadcrumbs from 'app/design/uiComponents/Breadcrumbs';
import { PAGES } from '../Common/Breadcrumb/types';
import FormattedMessage from 'app/design/uiComponents/FormattedMessage';
import { Box, Chip, CircularProgress, useTheme } from '@mui/material';
import { useGetCommunityIdea, useGetCommunityStrategy } from 'services/Community';
import { CommunityIdea, CommunityStrategy } from 'types/ApiServicesTypes';
import ContentBlocksDisplay from './ContentBlocksDisplay';

const PostDetailPage: React.FC = () => {
  const theme = useTheme();
  const { type, id } = useParams<{ type: string; id: string }>();
  const history = useHistory();
  const location = useLocation();
  const commentSectionRef = useRef<HTMLDivElement>(null);
  
  // Use appropriate hook based on post type
  const ideaQuery = useGetCommunityIdea(type === 'ideas' ? parseInt(id) : undefined);
  const strategyQuery = useGetCommunityStrategy(type === 'strategies' ? parseInt(id) : undefined);

  const handleBack = () => {
    history.push('/community');
  };

  // Get the post data based on type
  const post = type === 'ideas' ? ideaQuery.data?.data : strategyQuery.data?.data;
  const isLoading = type === 'ideas' ? ideaQuery.isLoading : strategyQuery.isLoading;
  const isError = type === 'ideas' ? ideaQuery.isError : strategyQuery.isError;

  // Handle hash fragment navigation to scroll to comment section
  useEffect(() => {
    if (location.hash === '#comments' && commentSectionRef.current && !isLoading) {
      // Add a small delay to ensure the component is fully rendered
      setTimeout(() => {
        commentSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [location.hash, isLoading]);

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (isError || !post) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <h2>Post not found</h2>
        <button onClick={handleBack}>Back to Community</button>
      </div>
    );
  }

  // Helper function to get tag name from tags array
  const getTagName = () => {
    if (post.tags && post?.tags?.length > 0) {
      return post.tags[0].name;
    }
    return 'GENERAL';
  };

  return (
    <Box>
       <PageHeader
            variant="paperTesting"
            buttonHandleClick={() => history.push('/community')}
            buttonText={
              <FormattedMessage id="portfolio.page_title.portfolio" />
            }
            title={post.title}
            value={''}
            chipProps={{
              variant: 'filled',
              label: post.category?.name,
              color:'infolight',
            }}
            breadcrumb={
              <Breadcrumbs
                page={PAGES.COMMUNITY}
                data={{ }}
              />
            }
          />
          <div style={{fontSize: 12, color: theme.palette.text.secondary, paddingInline: 24}}>
           {post.tags && post.tags.length > 0 && post.tags.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              sx={{
                marginRight: 1,
                marginBottom: 1,
              }}
              size="small"
              color="purplelight"
            />
           ))}
          </div>
          <div style={{fontSize: 12, color: theme.palette.text.secondary, paddingInline: 24}}>
            By {post.author.name} • {new Date(post.created_at).toLocaleString()}
          </div>

          <Box style={{padding: 24}}>
            {post?.images?.length > 0 && (
              <div style={{margin: '16px 0'}}>
                {post.images.map((img, idx) => (
                  <img key={idx} src={img} alt="post" style={{
                    width: '100%', 
                    maxHeight: 300, 
                    objectFit: 'cover', 
                    borderRadius: theme.shape.borderRadius, 
                    marginBottom: 8
                  }} />
                ))}
              </div>
            )}
            <div style={{margin: '16px 0'}}>
              <ContentBlocksDisplay contentBlocks={post.content_blocks || []} showFullContent={true}/>
            </div>
            <div ref={commentSectionRef}>
              <CommentSection post={post} />
            </div>
          </Box>
    </Box>
  );
};

export default PostDetailPage;
